import { useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Upload, Save, Send, ArrowLeft, Loader2, AlertCircle, CheckCircle2,
  MessageSquare, Plus, X, FileSpreadsheet, Users, ShieldCheck,
} from "lucide-react";
import {
  useExaminerGetResultsQuery,
  useExaminerSaveScoreMutation,
  useExaminerUploadExcelMutation,
  useExaminerSubmitResultsMutation,
  useExaminerGetFeedbackQuery,
} from "../../api/examinerApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

export default function ExaminerCourseResultsPage() {
  const { courseId = "", dietId = "" } = useParams<{ courseId: string; dietId: string }>();
  const navigate = useNavigate();

  const { data: results = [], isLoading: resultsLoading } = useExaminerGetResultsQuery(
    { courseId, dietId }, { skip: !courseId || !dietId }
  );
  const { data: feedback = [] } = useExaminerGetFeedbackQuery(
    { courseId, dietId }, { skip: !courseId || !dietId }
  );

  const [saveScore,      { isLoading: saving }]     = useExaminerSaveScoreMutation();
  const [uploadExcel,    { isLoading: uploading }]   = useExaminerUploadExcelMutation();
  const [submitResults,  { isLoading: submitting }]  = useExaminerSubmitResultsMutation();

  // Saving, uploading and submitting scores all call the examiner endpoints,
  // which require exam.marking — reading this page needs only grade access.
  const { can } = useAccess();
  const canMark = can.perm(PERMS.exam.marking);

  const [manualStudentId, setManualStudentId] = useState("");
  const [manualScore,     setManualScore]     = useState("");
  const [manualError,     setManualError]     = useState("");
  const [uploadError,     setUploadError]     = useState("");
  const [submitError,     setSubmitError]     = useState("");
  const [submitted,       setSubmitted]       = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const handleManualSave = async () => {
    setManualError("");
    if (!manualStudentId.trim()) { setManualError("Student ID is required"); return; }
    const score = parseFloat(manualScore);
    if (isNaN(score) || score < 0 || score > 100) { setManualError("Score must be 0–100"); return; }
    try {
      await saveScore({ courseId, dietId, studentId: manualStudentId.trim(), score }).unwrap();
      setManualStudentId(""); setManualScore("");
      setMsg({ type: "success", text: "Score saved" });
    } catch (err) { setManualError(extractError(err)); }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError("");
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("course_id", courseId);
    fd.append("diet_id", dietId);
    try {
      await uploadExcel(fd).unwrap();
      setMsg({ type: "success", text: "Excel uploaded successfully" });
    } catch (err) {
      setUploadError(extractError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    try {
      await submitResults({ courseId, dietId }).unwrap();
      setSubmitted(true); setShowConfirm(false);
    } catch (err) {
      setSubmitError(extractError(err)); setShowConfirm(false);
    }
  };

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  if (submitted) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20">
        <div className="bg-white border border-gray-100 rounded-3xl p-16 shadow-sm flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-3xl bg-emerald-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase mb-2">Results Submitted</h2>
          <p className="text-sm text-gray-400 font-medium mb-8 max-w-md">
            Your results have been submitted to the Chief Examiner for review. You will be notified if rework is required.
          </p>
          <button onClick={() => navigate("/exams/examiner")}
            className="bg-gray-900 text-white rounded-2xl px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95">
            Back to My Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Results Entered", val: (results as any[]).length,   icon: Users,         color: "indigo" },
          { label: "Feedback Items",  val: (feedback as any[]).length,  icon: MessageSquare, color: "amber"  },
          { label: "Ready to Submit", val: (results as any[]).length > 0 ? 1 : 0, icon: CheckCircle2, color: "emerald" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <Link to="/exams/examiner" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          </Link>
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <FileSpreadsheet className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Score Entry</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">Enter results via Excel upload or manual entry.</p>
          </div>
        </div>

        {msg && (
          <div className={`mt-6 p-4 rounded-2xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
            msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-rose-50 border-rose-100 text-rose-800"}`}>
            <span className="text-[10px] font-black uppercase tracking-widest">{msg.text}</span>
            <button onClick={() => setMsg(null)}><X className="w-4 h-4 opacity-50 hover:opacity-100" /></button>
          </div>
        )}

        {/* Chief feedback */}
        {(feedback as any[]).length > 0 && (
          <div className="mt-6 p-5 rounded-2xl border border-amber-100 bg-amber-50 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-amber-500" />
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">Chief Examiner Feedback</p>
            </div>
            {(feedback as any[]).map((f: any, i: number) => (
              <p key={i} className="text-sm text-amber-700 font-medium pl-6">{f.comment ?? f.message ?? f}</p>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left panel */}
        <div className="lg:col-span-1 space-y-5">

          {/* Excel Upload */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 border-b border-gray-50 pb-4">
              <Upload className="w-4 h-4 text-indigo-500" />
              <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">Bulk Upload</div>
            </div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
              Upload Excel with columns: <span className="text-gray-700">student_id</span> and <span className="text-gray-700">score</span>.
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" id="excel-upload" />
            <label htmlFor="excel-upload"
              className={`w-full inline-flex items-center justify-center gap-2 bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-100 active:scale-95 cursor-pointer ${uploading ? "opacity-30 pointer-events-none" : ""}`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Choose & Upload"}
            </label>
            {uploadError && (
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {uploadError}
              </p>
            )}
          </div>

          {/* Manual Entry */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 border-b border-gray-50 pb-4">
              <Plus className="w-4 h-4 text-indigo-500" />
              <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">Manual Entry</div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Student ID / Reg. No.</label>
              <input value={manualStudentId} onChange={(e) => setManualStudentId(e.target.value)}
                placeholder="e.g. NIQS/2024/001" className={inputCls} />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Score (0–100)</label>
              <input type="number" min={0} max={100} value={manualScore}
                onChange={(e) => setManualScore(e.target.value)} placeholder="0" className={inputCls} />
            </div>

            {manualError && (
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {manualError}
              </p>
            )}

            <button onClick={handleManualSave} disabled={saving || !canMark}
              className="w-full bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-100 disabled:opacity-30 active:scale-95 inline-flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Save Score"}
            </button>
          </div>
        </div>

        {/* Results table */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                Results ({(results as any[]).length})
              </div>
              {resultsLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
            </div>

            {resultsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
              </div>
            ) : (results as any[]).length === 0 ? (
              <div className="py-24 text-center">
                <Users className="w-10 h-10 mx-auto text-gray-100 mb-3" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No scores entered yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left bg-white border-b border-gray-50">
                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">#</th>
                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Student</th>
                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Reg. No.</th>
                    <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Score</th>
                    <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(results as any[]).map((r: any, i: number) => (
                    <tr key={r.id ?? i} className="hover:bg-gray-50/80 transition-all">
                      <td className="px-8 py-5 text-[10px] font-black text-gray-300">{i + 1}</td>
                      <td className="px-8 py-5">
                        <div className="font-black text-gray-900 uppercase tracking-tight">{r.student_name ?? r.student?.name ?? "—"}</div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{r.student_reg ?? r.reg_number ?? r.student_id ?? "—"}</div>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <span className="font-black text-gray-900 text-lg">{r.score ?? "—"}</span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm bg-gray-50 text-gray-400 border-gray-100">
                          {r.status ?? "draft"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Submit section */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        {submitError && (
          <div className="mb-5 p-4 rounded-2xl border bg-rose-50 border-rose-100 text-rose-800">
            <span className="text-[10px] font-black uppercase tracking-widest">{submitError}</span>
          </div>
        )}

        {!showConfirm ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-black text-gray-900 uppercase tracking-tight">Submit to Chief Examiner</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                Once submitted, scores will be locked for review.
              </p>
            </div>
            <button onClick={() => setShowConfirm(true)} disabled={(results as any[]).length === 0 || !canMark}
              className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-100 disabled:opacity-30 active:scale-95 shrink-0">
              <Send className="w-4 h-4" /> Submit Results
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-black text-gray-900 uppercase tracking-tight">Confirm Submission</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                Submit {(results as any[]).length} result{(results as any[]).length !== 1 ? "s" : ""}? This cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleSubmit} disabled={submitting}
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-emerald-600 transition-all shadow-lg disabled:opacity-30 active:scale-95">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Submitting…" : "Confirm"}
              </button>
              <button onClick={() => setShowConfirm(false)}
                className="py-3 px-5 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Submission Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Submitted results are reviewed by the Chief Examiner. If rework is requested, you will be notified
            and can make corrections before re-submitting.
          </p>
        </div>
      </div>
    </div>
  );
}
